/**
 * Role Management Service
 * Handles user role promotions and tenant migrations
 */

import apiClient from './apiClient';
import toast from 'react-hot-toast';
import { supabase } from '../config/supabase';

/**
 * Get all users with their tenant information
 * Grouped by tenant for easier management
 */
export const getAllUsersGroupedByTenant = async () => {
  try {
    if (import.meta.env.DEV) console.log('📋 Fetching all users grouped by tenant...');
    
    // First get all users
    const { data: users, error } = await supabase
      .from('users')
      .select('*')
      .order('tenant_id', { ascending: true })
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    if (!users || users.length === 0) {
      if (import.meta.env.DEV) console.log('✅ No users found');
      return { data: {}, error: null };
    }
    
    // Get unique tenant IDs
    const tenantIds = [...new Set(users.map(u => u.tenant_id).filter(Boolean))];
    
    // Fetch tenant information
    const { data: tenants, error: tenantError } = await supabase
      .from('tenants')
      .select('id, name, subdomain, location_city, location_country')
      .in('id', tenantIds);
    
    if (tenantError) {
      console.warn('Could not fetch tenant info:', tenantError);
    }
    
    // Create tenant lookup map
    const tenantMap = {};
    if (tenants) {
      tenants.forEach(t => {
        tenantMap[t.id] = t;
      });
    }
    
    // Attach tenant info to users
    users.forEach(user => {
      if (user.tenant_id && tenantMap[user.tenant_id]) {
        user.tenant = tenantMap[user.tenant_id];
      }
    });
    
    // Group users by tenant
    const grouped = {};
    users.forEach(user => {
      const tenantId = user.tenant_id || 'no-tenant';
      if (!grouped[tenantId]) {
        grouped[tenantId] = {
          tenant: user.tenant,
          users: []
        };
      }
      grouped[tenantId].users.push(user);
    });
    
    if (import.meta.env.DEV) console.log('✅ Users grouped by tenant:', Object.keys(grouped).length, 'tenants');
    return { data: grouped, error: null };
    
  } catch (error) {
    console.error('❌ Error fetching users:', error);
    return { data: null, error };
  }
};

/**
 * Promote user to Super Admin
 * WARNING: This gives platform-wide access!
 */
export const promoteToSuperAdmin = async (userId) => {
  try {
    if (import.meta.env.DEV) console.log('⬆️ Promoting user to Super Admin:', userId);
    
    // Call API endpoint
    const updatedUser = await apiClient.promoteToSuperAdmin(userId);
    
    if (import.meta.env.DEV) console.log('✅ User promoted to Super Admin:', updatedUser.email);
    toast.success(`User promoted to Super Admin! They now have platform-wide access.`);
    
    return { data: updatedUser, error: null };
    
  } catch (error) {
    console.error('❌ Error promoting to super admin:', error);
    toast.error(`Failed to promote user: ${error.message}`);
    return { data: null, error };
  }
};

/**
 * Promote physician to Global Physician (platform-level)
 * Allows them to see patients across all clinics
 */
export const promoteToGlobalPhysician = async (userId) => {
  try {
    if (import.meta.env.DEV) console.log('🌍 Promoting physician to Global Physician:', userId);
    
    // Call API endpoint
    const updatedUser = await apiClient.promoteToGlobalPhysician(userId);
    
    if (import.meta.env.DEV) console.log('✅ Physician promoted to Global Physician');
    toast.success(`Physician promoted to Global Physician! They can now see patients across all clinics.`);
    
    return { data: updatedUser, error: null };
    
  } catch (error) {
    console.error('❌ Error promoting to global physician:', error);
    toast.error(`Failed to promote physician: ${error.message}`);
    return { data: null, error };
  }
};

/**
 * Demote user from Super Admin
 * Moves them back to a specific tenant
 */
export const demoteFromSuperAdmin = async (userId, newTenantId, newRole) => {
  try {
    if (import.meta.env.DEV) console.log('⬇️ Demoting user from Super Admin:', userId);
    
    // Split update for RLS compatibility
    const { error: updateError } = await supabase
      .from('users')
      .update({
        platform_role: null,
        tenant_role: newRole,
        tenant_id: newTenantId,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (updateError) throw updateError;
    
    // Fetch updated user
    const { data: updatedUser, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (fetchError) throw fetchError;
    
    if (import.meta.env.DEV) console.log('✅ User demoted from Super Admin');
    
    toast.success(`User demoted. They now have ${newRole} access in their assigned clinic.`);
    
    return { data: updatedUser, error: null };
    
  } catch (error) {
    console.error('❌ Error demoting from super admin:', error);
    toast.error(`Failed to demote user: ${error.message}`);
    return { data: null, error };
  }
};

/**
 * Update user's role within their current tenant
 * (Does not change tenant assignment)
 */
export const updateUserRole = async (userId, newTenantRole) => {
  try {
    if (import.meta.env.DEV) console.log('🔄 Updating user role:', userId, '->', newTenantRole);
    
    const { data: updatedUser, error: updateError } = await supabase
      .from('users')
      .update({
        tenant_role: newTenantRole,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select(`
        *,
        tenant:tenants(id, name, subdomain)
      `)
      .single();
    
    if (updateError) throw updateError;
    
    if (import.meta.env.DEV) console.log('✅ User role updated');
    
    toast.success(`User role updated to ${newTenantRole}`);
    
    return { data: updatedUser, error: null };
    
  } catch (error) {
    console.error('❌ Error updating user role:', error);
    toast.error(`Failed to update role: ${error.message}`);
    return { data: null, error };
  }
};

/**
 * Change user's clinic affiliation (tenant)
 * Super Admin only - moves user to different clinic
 */
export const changeUserTenant = async (userId, newTenantId) => {
  try {
    if (import.meta.env.DEV) console.log('🏢 Changing user tenant:', userId, '->', newTenantId);
    
    // Update user's tenant (split for RLS)
    const { error: updateError } = await supabase
      .from('users')
      .update({
        tenant_id: newTenantId,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (updateError) throw updateError;
    
    // Fetch updated user
    const { data: updatedUser, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (fetchError) throw fetchError;
    
    if (import.meta.env.DEV) console.log('✅ User clinic affiliation updated');
    
    toast.success('User moved to new clinic successfully!');
    
    return { data: updatedUser, error: null };
    
  } catch (error) {
    console.error('❌ Error changing user tenant:', error);
    toast.error(`Failed to change clinic: ${error.message}`);
    return { data: null, error };
  }
};

/**
 * Get role display information
 */
export const getRoleDisplay = (user) => {
  // Super admin
  if (user.platform_role === 'super_admin') {
    return {
      label: 'Super Admin',
      color: 'error',
      icon: '👑',
      access: 'Platform-Wide',
      level: 'platform'
    };
  }
  
  // Global physician
  if (user.platform_role === 'global_physician') {
    return {
      label: 'Global Physician',
      color: 'primary',
      icon: '🌍',
      access: 'All Clinics',
      level: 'platform'
    };
  }
  
  // Tenant-level roles
  const roleMap = {
    'clinic_admin': { label: 'Clinic Admin', color: 'secondary', icon: '⚙️', access: 'Clinic Only', level: 'tenant' },
    'clinic_staff': { label: 'Clinic Staff', color: 'info', icon: '👨‍⚕️', access: 'Clinic Only', level: 'tenant' },
    'physician': { label: 'Physician', color: 'primary', icon: '🩺', access: 'Clinic Only', level: 'tenant' },
    'sponsor': { label: 'Sponsor', color: 'success', icon: '💝', access: 'Cross-Clinic', level: 'platform' },
    'patient': { label: 'Patient', color: 'default', icon: '👤', access: 'Personal Only', level: 'tenant' }
  };
  
  return roleMap[user.tenant_role] || {
    label: user.tenant_role,
    color: 'default',
    icon: '?',
    access: 'Unknown',
    level: 'unknown'
  };
};
